'use client';

/**
 * Authentication Provider for VisualGrid
 * Manages user authentication state, profile, and plan status
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, Profile } from './supabaseClient';
import { toast } from 'sonner';
import { isTauriApp, startTauriGoogleOAuth, initDeepLinkAuthListener } from './tauri-auth';
import { getApiUrl } from './api-url';
import { useProEntitlement } from '@/hooks/use-pro-entitlement';
import { identifyUser, resetPostHog, captureEvent } from './posthog-helper';

interface AuthContextType {
  // User state
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;

  // Plan state
  plan: 'free' | 'pro';
  isPro: boolean;
  isSubscriptionActive: boolean;

  // Actions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  refreshProfile: () => Promise<void>;

  // Offline/Grace period
  cachedPlan: 'free' | 'pro' | null;
  gracePeriodActive: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CACHE_KEY_PLAN = 'vg_cached_plan';
const CACHE_KEY_PERIOD_END = 'vg_period_end';
const GRACE_PERIOD_HOURS = 72;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [cachedPlan, setCachedPlan] = useState<'free' | 'pro' | null>(null);
  const [gracePeriodActive, setGracePeriodActive] = useState(false);
  const loadingWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  const hasGeneratedProEntitlementRef = useRef(false);

  // Check if we're in Electron
  const isElectron = typeof window !== 'undefined' && (window as any).electron;

  /**
   * Store token securely (Electron) or fallback to localStorage
   */
  const storeRefreshToken = useCallback(async (token: string) => {
    if (isElectron && (window as any).auth?.storeToken) {
      try {
        await (window as any).auth.storeToken(token);
      } catch (error) {
        localStorage.setItem('vg_refresh_token', token);
      }
    } else {
      localStorage.setItem('vg_refresh_token', token);
    }
  }, [isElectron]);

  /**
   * Get stored token
   */
  const getStoredToken = useCallback(async (): Promise<string | null> => {
    if (isElectron && (window as any).auth?.getToken) {
      try {
        return await (window as any).auth.getToken();
      } catch (error) {
        return localStorage.getItem('vg_refresh_token');
      }
    }
    return localStorage.getItem('vg_refresh_token');
  }, [isElectron]);

  /**
   * Clear stored token
   */
  const clearStoredToken = useCallback(async () => {
    if (isElectron && (window as any).auth?.clearToken) {
      try {
        await (window as any).auth.clearToken();
      } catch (error) {
        // Silent error
      }
    }
    localStorage.removeItem('vg_refresh_token');
  }, [isElectron]);

  /**
   * Load user profile — dual path:
   * - Web: via server-side API /api/auth/profile (immune to zombie client)
   * - Tauri: via direct Supabase calls (no Next.js server in static export)
   *   Protected by P0 setTimeout fix against deadlock.
   * @param accessToken - optional token override for cases where session state isn't yet updated
   */
  const loadProfile = useCallback(async (userId: string, skipIfLoaded: boolean = false, accessToken?: string): Promise<Profile | null> => {
    try {
      // ⚡ Optimization: Skip if already loaded
      if (skipIfLoaded && profile?.id === userId) {
        return profile;
      }

      // ── Tauri path: direct Supabase calls (no API routes in static export) ──
      if (isTauriApp()) {
        return await loadProfileDirect(userId);
      }

      // ── Web path: server-side API (immune to zombie client) ──
      const token = accessToken || session?.access_token;
      if (!token) {
        return null;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(getApiUrl('/api/auth/profile'), {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return null;
      }

      const { profile: data, subscriptionEnd } = await response.json();

      if (data) {
        localStorage.setItem(CACHE_KEY_PLAN, data.plan);
        setCachedPlan(data.plan);

        if (subscriptionEnd) {
          localStorage.setItem(CACHE_KEY_PERIOD_END, subscriptionEnd);
        }
      }

      return data as Profile;
    } catch (error) {
      return null;
    }
  }, [profile, session?.access_token]);

  /**
   * Direct Supabase profile loading — used only in Tauri (no API routes).
   * Safe because all onAuthStateChange calls are deferred via setTimeout (P0 fix).
   * En mode offline, utilise le système d'entitlement Pro si disponible.
   */
  const loadProfileDirect = async (userId: string): Promise<Profile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.log('❌ Error loading profile from Supabase:', error.message);
        // Si erreur de connexion, essayer le système d'entitlement Pro offline
        if (error.message?.includes('network') || error.message?.includes('fetch')) {
          console.log('🔄 Trying offline Pro entitlement fallback...');
          try {
            // Essayer de vérifier le statut Pro via Tauri
            if (typeof window !== 'undefined' && (window as any).__TAURI__) {
              const { invoke } = await import('@tauri-apps/api/core');
              const proStatus = await invoke('get_pro_status') as any;
              console.log('📊 Pro status from Tauri:', proStatus);

              if (proStatus && proStatus.is_valid && proStatus.is_pro) {
                console.log('✅ Using offline Pro entitlement');
                const authUser = user || session?.user;
                const displayName = authUser?.user_metadata?.display_name
                  || authUser?.user_metadata?.full_name
                  || authUser?.email?.split('@')[0]
                  || 'User';

                return {
                  id: userId,
                  email: authUser?.email || '',
                  display_name: displayName,
                  plan: 'pro',
                  stripe_customer_id: null,
                  updated_at: new Date().toISOString(),
                  preferred_language: 'en',
                };
              } else {
                console.log('⚠️ Pro status invalid or not Pro');
              }
            } else {
              console.log('⚠️ Not running in Tauri');
            }
          } catch (tauriError) {
            // Silent fallback si le système Tauri échoue
            console.log('❌ Tauri Pro check failed:', tauriError);
          }
        }

        if (error.code === 'PGRST116') {
          const { data: { user: authUser } } = await supabase.auth.getUser();

          if (authUser) {
            const displayName = authUser.user_metadata?.display_name
              || authUser.user_metadata?.full_name
              || authUser.email?.split('@')[0]
              || 'User';

            const { data: newProfile, error: insertError } = await supabase
              .from('profiles')
              .insert({
                id: userId,
                email: authUser.email!,
                display_name: displayName,
                plan: 'free',
              })
              .select()
              .single();

            if (insertError) {
              return null;
            }
            localStorage.setItem(CACHE_KEY_PLAN, 'free');
            setCachedPlan('free');
            return newProfile as Profile;
          }
        }
        return null;
      }

      if (data) {
        localStorage.setItem(CACHE_KEY_PLAN, data.plan);
        setCachedPlan(data.plan);

        if (data.plan === 'pro') {
          (async () => {
            try {
              const { data: subData, error: subError } = await supabase
                .from('subscriptions')
                .select('current_period_end')
                .eq('user_id', userId)
                .eq('status', 'active')
                .maybeSingle();

              if (!subError && subData?.current_period_end) {
                localStorage.setItem(CACHE_KEY_PERIOD_END, subData.current_period_end);
              }
            } catch {
              // Table subscriptions may not exist yet — fail silently
            }
          })();
        }
      }

      return data as Profile;
    } catch (error) {
      return null;
    }
  };

  /**
   * Check grace period for offline Pro access
   */
  const checkGracePeriod = () => {
    const cached = localStorage.getItem(CACHE_KEY_PLAN);
    const periodEnd = localStorage.getItem(CACHE_KEY_PERIOD_END);

    if (cached === 'pro' && periodEnd) {
      const endDate = new Date(periodEnd);
      const graceEnd = new Date(endDate.getTime() + GRACE_PERIOD_HOURS * 60 * 60 * 1000);
      const now = new Date();

      if (now < graceEnd) {
        setGracePeriodActive(true);
        setCachedPlan('pro');
        return true;
      }
    }

    setGracePeriodActive(false);
    return false;
  };

  /**
   * Initialize auth on mount
   */
  useEffect(() => {
    let mounted = true;
    let isInitializing = true; // Flag to prevent duplicate loads

    const initAuth = async () => {
      try {
        // Check if Supabase is configured
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
        
        if (!supabaseUrl || !supabaseKey) {
          // Set free plan by default when no auth is configured
          setCachedPlan('free');
          if (mounted) {
            setLoading(false);
          }
          return;
        }
        
        // Try to get existing session
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (existingSession) {
          if (mounted) {
            setSession(existingSession);
            setUser(existingSession.user);
            const userProfile = await loadProfile(existingSession.user.id, false, existingSession.access_token);
            setProfile(userProfile);
          }
        } else {
          // Try to restore from stored token
          const storedToken = await getStoredToken();
          if (storedToken) {
            const { data, error } = await supabase.auth.refreshSession({
              refresh_token: storedToken,
            });

            if (data.session && !error && mounted) {
              setSession(data.session);
              setUser(data.session.user);
              const userProfile = await loadProfile(data.session.user.id, false, data.session.access_token);
              setProfile(userProfile);
              await storeRefreshToken(data.session.refresh_token);
            } else if (error) {
              await clearStoredToken();
              checkGracePeriod(); // Activate grace period if applicable
            }
          } else {
            // No session, check if grace period applies
            checkGracePeriod();
          }
        }
      } catch (error) {
        checkGracePeriod();
      } finally {
        if (mounted) {
          setLoading(false);
          isInitializing = false; // Mark initialization as complete
        }
      }
    };

    initAuth();

    // Tauri deep link listener for OAuth callback
    let unlistenDeepLink: (() => void) | null = null;
    if (isTauriApp()) {
      initDeepLinkAuthListener(
        async (deepLinkSession) => {
          if (!mounted) return;
          setSession(deepLinkSession);
          setUser(deepLinkSession.user);
          const userProfile = await loadProfile(deepLinkSession.user.id, false, deepLinkSession.access_token);
          setProfile(userProfile);
          await storeRefreshToken(deepLinkSession.refresh_token);
          setGracePeriodActive(false);
          toast.success('Connexion Google réussie !');
        },
        (error) => {
          if (!mounted) return;
          toast.error(error.message || 'Erreur de connexion Google');
        },
      ).then((unlisten) => {
        unlistenDeepLink = unlisten;
      });
    }

    // Listen for auth changes
    // ⚠️ CRITICAL FIX: callback MUST be synchronous — async Supabase calls inside
    // onAuthStateChange cause a deadlock in supabase-js (see auth-js#762, supabase#36046).
    // All Supabase/async operations are deferred via setTimeout(..., 0).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;

      if (event === 'SIGNED_IN' && newSession) {
        // ⚡ Optimization: Skip during initialization to avoid double loading
        if (isInitializing) {
          return;
        }
        
        setSession(newSession);
        setUser(newSession.user);
        setGracePeriodActive(false);
        // ✅ Defer Supabase calls to avoid deadlock
        setTimeout(async () => {
          if (!mounted) return;
          const userProfile = await loadProfile(newSession.user.id, true, newSession.access_token);
          if (mounted) setProfile(userProfile);
          await storeRefreshToken(newSession.refresh_token);

          // ✅ PostHog: Track OAuth success (login or signup)
          // Detect if it's OAuth by checking the provider
          const isOAuth = newSession.user?.app_metadata?.provider === 'google';
          
          if (isOAuth) {
            // Check if it's a new user (signup) or returning user (login)
            // If profile is null or was just created, it's a signup
            const isNewUser = userProfile === null;
            
            identifyUser(newSession.user, userProfile);
            
            if (isNewUser) {
              captureEvent('auth:signup_success', {
                auth_method: 'google',
                has_display_name: !!newSession.user.user_metadata?.display_name,
              });
            } else {
              captureEvent('auth:login_success', {
                auth_method: 'google',
                is_returning_user: true,
              });
            }
          }
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        checkGracePeriod();
        // ✅ Defer async token cleanup
        setTimeout(async () => {
          await clearStoredToken();
        }, 0);
      } else if (event === 'TOKEN_REFRESHED' && newSession) {
        setSession(newSession);
        // ✅ Defer async token storage
        setTimeout(async () => {
          await storeRefreshToken(newSession.refresh_token);
        }, 0);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (unlistenDeepLink) unlistenDeepLink();
    };
  }, []); // Empty deps - initAuth only runs once on mount

  /**
   * Rafraîchir la session quand l'utilisateur revient sur l'onglet
   * Résout le problème des tokens expirés après inactivité
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user) {
        try {
          // ✅ FIX: Use timeout to prevent hanging if Supabase client is in zombie state
          const timeoutMs = 8000;
          const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));
          
          const refreshResult = await Promise.race([
            supabase.auth.refreshSession(),
            timeoutPromise
          ]);
          
          // Timeout — client may be in zombie state, skip silently
          if (!refreshResult) return;
          
          const { data, error } = refreshResult as { data: any; error: any };
          if (error) {
            // Essayer de récupérer depuis le storage
            const storedToken = await getStoredToken();
            if (storedToken) {
              const refreshRetry = await Promise.race([
                supabase.auth.refreshSession({ refresh_token: storedToken }),
                timeoutPromise
              ]);
              if (refreshRetry) {
                const { data: refreshData, error: refreshError } = refreshRetry as { data: any; error: any };
                if (refreshData?.session && !refreshError) {
                  setSession(refreshData.session);
                  setUser(refreshData.session.user);
                  await storeRefreshToken(refreshData.session.refresh_token);
                } else {
                  toast.warning('Session expirée. Veuillez vous reconnecter.');
                }
              }
            } else {
              toast.warning('Session expirée. Veuillez vous reconnecter.');
            }
          } else if (data?.session) {
            setSession(data.session);
            await storeRefreshToken(data.session.refresh_token);
          }
        } catch (e) {
          // Silent error — don't block UI
        }
      }
    };

    // Rafraîchir immédiatement au focus (plus réactif que visibilitychange seul)
    const handleFocus = () => {
      if (user) {
        handleVisibilityChange();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, getStoredToken, storeRefreshToken]);

  /**
   * Loading watchdog: force loading to false after 15s
   */
  useEffect(() => {
    if (loading) {
      loadingWatchdogRef.current = setTimeout(() => {
        setLoading(false);
        // Try grace period as fallback
        checkGracePeriod();
      }, 15000);
    } else if (loadingWatchdogRef.current) {
      clearTimeout(loadingWatchdogRef.current);
      loadingWatchdogRef.current = null;
    }

    return () => {
      if (loadingWatchdogRef.current) {
        clearTimeout(loadingWatchdogRef.current);
      }
    };
  }, [loading]);

  /**
   * Sign in with email/password
   */
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        // Update state immediately for better UX
        setSession(data.session);
        setUser(data.session.user);

        // Load and set profile
        const userProfile = await loadProfile(data.session.user.id, false, data.session.access_token);
        setProfile(userProfile);

        // Store refresh token for persistence
        await storeRefreshToken(data.session.refresh_token);

        // ✅ PostHog: Identify user after successful login
        identifyUser(data.session.user, userProfile);
        captureEvent('auth:login_success', {
          auth_method: 'email_password',
          is_returning_user: userProfile !== null,
        });

        toast.success('Connexion réussie !');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erreur de connexion');
      throw error;
    }
  }, [storeRefreshToken, loadProfile]);

  /**
   * Sign up with email/password
   */
  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName || email.split('@')[0],
          },
        },
      });

      if (error) throw error;

      // Create profile
      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            email: data.user.email!,
            display_name: displayName || email.split('@')[0],
            plan: 'free',
          });

        // ✅ PostHog: Track signup success
        captureEvent('auth:signup_success', {
          auth_method: 'email_password',
          has_display_name: !!displayName,
        });

        toast.success('Compte créé ! Vérifiez votre email.');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la création du compte');
      throw error;
    }
  }, []);

  /**
   * Sign in with Google OAuth
   */
  const signInWithGoogle = useCallback(async () => {
    try {
      // In Tauri desktop app: use deep link flow (browser + deep link callback)
      if (isTauriApp()) {
        await startTauriGoogleOAuth();
        // Session will be set via the deep link listener in initAuth
        return;
      }

      // Web: standard OAuth redirect flow
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || 'Erreur de connexion avec Google');
      throw error;
    }
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    let hadRemoteSignOutError = false;

    try {
      // Global sign out can fail intermittently (network/session race).
      // We still want a deterministic local logout for UX.
      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        hadRemoteSignOutError = true;
        // Fallback: clear local session even if global revoke failed.
        const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
        if (localError) throw localError;
      }

      await clearStoredToken();
      localStorage.removeItem(CACHE_KEY_PLAN);
      localStorage.removeItem(CACHE_KEY_PERIOD_END);
      setSession(null);
      setUser(null);
      setProfile(null);

      // ✅ PostHog: Reset identification on logout
      resetPostHog();
      captureEvent('auth:logout', {
        session_duration_seconds: 0, // TODO: Calculate actual session duration
      });
      setCachedPlan(null);
      setGracePeriodActive(false);

      if (hadRemoteSignOutError) {
        toast.success('Déconnexion locale réussie');
      } else {
        toast.success('Déconnexion réussie');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erreur de déconnexion');
      throw error;
    }
  }, [clearStoredToken]);

  /**
   * Refresh authentication and profile
   */
  const refreshAuth = useCallback(async () => {
    if (!user) return;

    try {
      const userProfile = await loadProfile(user.id);
      setProfile(userProfile);
    } catch (error) {
      // Silent error
    }
  }, [user, loadProfile]);

  /**
   * Refresh profile only (alias for refreshAuth)
   */
  const refreshProfile = refreshAuth;

  // Derive plan status
  const plan = profile?.plan || cachedPlan || 'free';
  const isPro = plan === 'pro';
  const isSubscriptionActive = isPro && !gracePeriodActive;

  // Helper pour générer un device ID
  const getDeviceId = async (): Promise<string> => {
    const stored = localStorage.getItem('device_id');
    if (stored) return stored;

    const newId = crypto.randomUUID();
    localStorage.setItem('device_id', newId);
    return newId;
  };

  // Générer automatiquement le jeton d'entitlement Pro quand l'utilisateur est en ligne et Pro
  useEffect(() => {
    const generateProEntitlement = async () => {
      if (!user || !session || !isPro || !isTauriApp() || hasGeneratedProEntitlementRef.current) {
        return;
      }

      try {
        const response = await fetch('/api/pro/entitlement', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            deviceId: await getDeviceId(),
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.token) {
            const { invoke } = await import('@tauri-apps/api/core');
            await invoke('store_pro_token', { token: data.token });
            hasGeneratedProEntitlementRef.current = true;
            console.log('✅ Jeton d\'entitlement Pro généré et stocké');
          }
        }
      } catch (error) {
        console.log('Erreur lors de la génération du jeton d\'entitlement:', error);
      }
    };

    generateProEntitlement();
  }, [user, session, isPro]);

  const value: AuthContextType = {
    user,
    profile,
    session,
    loading,
    plan,
    isPro,
    isSubscriptionActive,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    refreshAuth,
    refreshProfile,
    cachedPlan,
    gracePeriodActive,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

